import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Bell, BellOff } from 'lucide-react';
import { useAuth } from '../App';
import { ensurePushSubscription, removePushSubscription } from '../utils/push';

const OFFICE_ROLES = ['admin', 'customer_service', 'bookings_operator', 'invoice_operator'];
const PUSH_ROLES = [...OFFICE_ROLES, 'property_responsible'];

const NotificationManager = () => {
  const { user } = useAuth();
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  const isOffice = user && PUSH_ROLES.includes(user.role);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then((registration) => registration.pushManager?.getSubscription())
        .then((subscription) => setIsSubscribed(!!subscription))
        .catch(() => {});
    }
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  // Se il permesso è già concesso, assicura la sottoscrizione push (silenzioso)
  useEffect(() => {
    if (!isOffice || notificationPermission !== 'granted') return;
    ensurePushSubscription().then((ok) => { if (ok) setIsSubscribed(true); }).catch(() => {});
  }, [isOffice, notificationPermission]);

  const enablePush = async () => {
    if (!('Notification' in window)) return;
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === 'granted') {
        const ok = await ensurePushSubscription();
        setIsSubscribed(ok);
      }
    } catch (_) { /* niente */ }
    finally { setBusy(false); }
  };

  const disablePush = async () => {
    setBusy(true);
    await removePushSubscription();
    setIsSubscribed(false);
    setBusy(false);
  };

  if (!isOffice) return null;

  return (
    <div className="flex shrink-0 items-center gap-2">
      {notificationPermission !== 'denied' && !isSubscribed && (
        <Button variant="outline" size="sm" onClick={enablePush} disabled={busy}
          data-testid="push-enable-btn" className="flex items-center gap-2">
          <Bell className="h-4 w-4" />
          {user?.role === 'property_responsible' ? 'Attiva notifiche prenotazioni' : 'Attiva notifiche chiamate'}
        </Button>
      )}

      {isSubscribed && (
        <span className="flex items-center gap-1.5 text-xs text-emerald-700" data-testid="push-active-badge">
          <Bell className="h-3.5 w-3.5" /> {user?.role === 'property_responsible' ? 'Notifiche prenotazioni attive' : 'Notifiche chiamate attive'}
          <button onClick={disablePush} title="Disattiva su questo dispositivo"
            data-testid="push-disable-btn" className="text-slate-400 hover:text-red-500 ml-1">
            <BellOff className="h-3.5 w-3.5" />
          </button>
        </span>
      )}

      {notificationPermission === 'denied' && (
        <span
          className="inline-flex items-center gap-1 text-xs text-red-600"
          title="Notifiche bloccate. Abilita dalle impostazioni del browser."
          data-testid="push-blocked-badge"
        >
          <BellOff className="h-4 w-4" />
          <span className="hidden lg:inline">Notifiche bloccate. Abilita dalle impostazioni del browser.</span>
        </span>
      )}
    </div>
  );
};

export default NotificationManager;
